import { useEffect, useRef, useState, useCallback } from 'react';
import typewriterClickUrl from '@/assets/sounds/typewriter-click.wav';

interface AudioSystemOptions {
  volume?: number;
  enabled?: boolean;
}

// Singleton audio context and buffer to prevent multiple loads
let sharedAudioContext: AudioContext | null = null;
let sharedAudioBuffer: AudioBuffer | null = null;
let isLoading = false;
let loadPromise: Promise<AudioBuffer | null> | null = null;

const getSharedAudioContext = (): AudioContext | null => {
  if (sharedAudioContext && sharedAudioContext.state !== 'closed') {
    return sharedAudioContext;
  }
  
  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextClass) return null;
  
  sharedAudioContext = new AudioContextClass();
  return sharedAudioContext;
};

const loadSharedAudio = async (context: AudioContext): Promise<AudioBuffer | null> => {
  // Return cached buffer if already loaded
  if (sharedAudioBuffer) return sharedAudioBuffer;
  
  // Return existing promise if already loading
  if (loadPromise) return loadPromise;
  
  // Prevent multiple simultaneous loads
  if (isLoading) return null;
  
  isLoading = true;
  
  loadPromise = (async () => {
    try {
      const response = await fetch(typewriterClickUrl);
      const arrayBuffer = await response.arrayBuffer();
      sharedAudioBuffer = await context.decodeAudioData(arrayBuffer);
      return sharedAudioBuffer;
    } catch (error) {
      console.warn('Failed to load audio:', error);
      return null;
    } finally {
      isLoading = false;
    }
  })();
  
  return loadPromise;
};

export const useAudioSystem = (options: AudioSystemOptions = {}) => {
  const [isEnabled, setIsEnabled] = useState(() => {
    // Check localStorage and system preferences
    const stored = localStorage.getItem('audio-enabled');
    if (stored !== null) return stored === 'true';
    
    // Respect prefers-reduced-motion - auto-disable audio
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // Check prefers-reduced-transparency (some users want minimal effects)
    const prefersReducedTransparency = window.matchMedia('(prefers-reduced-transparency: reduce)').matches;
    
    return !prefersReduced && !prefersReducedTransparency && (options.enabled ?? true);
  });

  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const lastPlayTimeRef = useRef<number>(0);
  const { volume = 0.25 } = options;

  useEffect(() => {
    if (!isEnabled) return;

    // Use shared audio context
    const context = getSharedAudioContext();
    if (!context) return;
    
    audioContextRef.current = context;

    // Load audio (will use cache if already loaded)
    loadSharedAudio(context).then(buffer => {
      if (buffer) {
        audioBufferRef.current = buffer;
      }
    });

    // Don't close shared context on unmount
  }, [isEnabled]);

  const playSound = useCallback((volumeVariation = 0) => {
    if (!isEnabled || !audioContextRef.current || !audioBufferRef.current) return;
    
    // Respect document visibility - don't play when tab is hidden
    if (document.visibilityState !== 'visible') return;

    // Debounce replays - minimum 150ms between sounds
    const now = performance.now();
    if (now - lastPlayTimeRef.current < 150) return;
    lastPlayTimeRef.current = now;

    try {
      // Resume context if suspended (for mobile autoplay restrictions)
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }

      const source = audioContextRef.current.createBufferSource();
      const gainNode = audioContextRef.current.createGain();

      source.buffer = audioBufferRef.current;
      
      // Add slight volume variation for natural feel (±10%)
      const variation = 1 + (Math.random() - 0.5) * volumeVariation;
      gainNode.gain.value = volume * variation;

      source.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      source.start(0);
    } catch (error) {
      console.warn('Failed to play sound:', error);
    }
  }, [isEnabled, volume]);

  const toggleAudio = useCallback(() => {
    const newState = !isEnabled;
    setIsEnabled(newState);
    localStorage.setItem('audio-enabled', String(newState));
  }, [isEnabled]);

  return {
    playSound,
    isEnabled,
    toggleAudio,
  };
};
